import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { createCheckout, fetchProducts } from "@/lib/api";
import { getProject } from "../data/projects";

const EASE = [0.16, 1, 0.3, 1];
const money = (cents) => `$${(cents / 100).toLocaleString()}`;

export default function Purchase() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const project = getProject(slug);
  const initialOption = params.get("option");
  const [option, setOption] = useState(initialOption === "waas" || initialOption === "own" ? initialOption : null);
  const [prices, setPrices] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const canceled = params.get("canceled") === "1";

  useEffect(() => {
    fetchProducts()
      .then(({ data }) => {
        const p = data.products.find((x) => x.slug === slug);
        if (p) setPrices({ own: p.own_it_cents, waas: p.waas_monthly_cents });
      })
      .catch(() => {});
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen bg-obsidian px-5 pt-40 text-white md:px-10">
        <p className="text-fog">Unknown build.</p>
        <Link to="/work" className="mt-4 inline-block text-white underline underline-offset-8">Back to Work</Link>
      </div>
    );
  }

  // Server catalog is authoritative; project file is the display fallback.
  const ownCents = prices?.own ?? (project.priceOnce ? project.priceOnce * 100 : null);
  const waasCents = prices?.waas ?? (project.priceMonthly ? project.priceMonthly * 100 : null);

  const pay = async (chosen) => {
    setSubmitting(true);
    try {
      const { data } = await createCheckout({
        slug,
        purchase_type: chosen,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout — try again.");
      setSubmitting(false);
    }
  };

  const cards = [
    {
      id: "own",
      title: "Own It",
      price: ownCents != null ? money(ownCents) : "—",
      cadence: "one time",
      lines: ["Your website. Your business. Your brand.", "Customized and launched for you.", "One payment. It's yours."],
    },
    {
      id: "waas",
      title: "Website as a Service",
      price: waasCents != null ? money(waasCents) : "—",
      cadence: "per month",
      lines: ["Your website, customized and launched.", "No upfront purchase price.", "No long-term commitment."],
      disclosure: "Cancel with at least 30 days' notice before your next billing date.",
    },
  ];

  const chosen = cards.find((c) => c.id === option);

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="mx-auto max-w-3xl px-5 pb-32 pt-28 md:px-10 md:pt-36">
        <Link to={`/work/${slug}`} data-testid="purchase-back-link" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-0.5" />
          {project.name}
        </Link>
        <p className="mt-10 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
          Make {project.name} yours
        </p>

        {canceled && (
          <p data-testid="purchase-canceled-note" className="mt-8 border border-white/15 p-4 text-sm text-fog">
            Payment was canceled — nothing was charged. Pick up where you left off whenever you're ready.
          </p>
        )}

        <AnimatePresence mode="wait">
          {!option ? (
            <motion.div key="choose" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5, ease: EASE }}>
              <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-6xl">
                Choose how to make it yours.
              </h1>
              <div className="mt-12 grid gap-5 md:grid-cols-2">
                {cards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    data-testid={`option-${card.id}`}
                    onClick={() => setOption(card.id)}
                    className="group flex flex-col justify-between border border-white/15 p-6 text-left transition-colors duration-500 hover:border-white md:p-8"
                  >
                    <div>
                      <p className="font-display text-2xl font-medium tracking-[-0.02em]">{card.title}</p>
                      <p className="mt-5 font-display text-4xl font-medium tracking-[-0.02em]">
                        {card.price}
                        <span className="ml-2 text-sm font-normal text-fog">{card.cadence}</span>
                      </p>
                      <ul className="mt-6 space-y-2.5">
                        {card.lines.map((l) => (
                          <li key={l} className="flex gap-3 text-sm leading-relaxed text-fog">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/70" />
                            {l}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-6">
                      {card.disclosure && (
                        <p className="text-xs leading-relaxed text-fog/70">{card.disclosure}{" "}
                          <Link to="/manage" data-testid="subscription-terms-link" onClick={(e) => e.stopPropagation()} className="underline underline-offset-4 transition-colors hover:text-white">
                            Subscription terms
                          </Link>
                        </p>
                      )}
                      <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white">
                        Continue <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="pay" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5, ease: EASE }}>
              <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-6xl">
                {option === "own" ? "One payment. It's yours." : "Your website, monthly."}
              </h1>
              <div className="mt-12 border border-white/15 p-6 md:p-8" data-testid="review-summary">
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-display text-2xl font-medium tracking-[-0.02em]">{project.name}</p>
                  <p className="font-display text-3xl font-medium tracking-[-0.02em]" data-testid="review-amount">
                    {option === "own" ? money(ownCents) : money(waasCents)}
                    <span className="ml-2 text-sm font-normal text-fog">{option === "own" ? "one time" : "/ month"}</span>
                  </p>
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.3em] text-fog" data-testid="review-option">
                  {chosen.title}
                </p>
                {option === "waas" && (
                  <p className="mt-6 border-t border-white/10 pt-6 text-xs leading-relaxed text-fog/80" data-testid="review-terms">
                    {money(waasCents)} per month, billed monthly. No minimum commitment. Cancel with at least 30 days'
                    notice before your next billing date.{" "}
                    <Link to="/manage" className="text-white underline underline-offset-4">Subscription terms</Link>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => pay(option)}
                disabled={submitting}
                data-testid="pay-button"
                className="group mt-8 flex w-full items-center justify-center gap-3 border border-white bg-white px-6 py-5 text-xs font-semibold uppercase tracking-[0.25em] text-obsidian transition-colors duration-500 ease-cinematic hover:bg-transparent hover:text-white disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" />
                {submitting ? "Opening secure checkout…" : `Pay ${option === "own" ? money(ownCents) : `${money(waasCents)} / month`} securely`}
              </button>
              <p className="mt-4 text-center text-xs text-fog/50">
                Secure payment by Stripe. You're not charged until you confirm on Stripe's page.
              </p>
              <button type="button" onClick={() => setOption(null)} data-testid="purchase-change-option"
                className="group mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors hover:text-white">
                <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-0.5" /> Choose a different option
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
